import { useEffect, useState } from 'react';
import { ConfirmationModal } from '../shared/ConfirmationModal';
import { messagingApi } from '../../services/messaging-api';

type MessageTemplatesPageProps = {
  tenantId?: string;
  onNavigate?: (page: string) => void;
};

type MessageTemplateForm = {
  tenantId: string;
  name: string;
  channel: 'SMS' | 'EMAIL';
  subject: string;
  body: string;
  category: string;
};

export function MessageTemplatesPage({ tenantId = '', onNavigate }: MessageTemplatesPageProps) {
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(null);
  const [deleteTemplateCandidate, setDeleteTemplateCandidate] = useState<{ id: string; name: string } | null>(null);
  const [formData, setFormData] = useState<MessageTemplateForm>({
    tenantId,
    name: '',
    channel: 'SMS',
    subject: '',
    body: '',
    category: '',
  });

  useEffect(() => {
    loadTemplates();
  }, [tenantId]);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const response = await messagingApi.getTemplates(tenantId);
      setTemplates(response.data || []);
      setError(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditTemplate = (template: any) => {
    setEditingTemplateId(template.id);
    setFormData({
      tenantId,
      name: template.name || '',
      channel: template.channel || 'SMS',
      subject: template.subject || '',
      body: template.body || '',
      category: template.category || '',
    });
  };

  const handleCancelEdit = () => {
    setEditingTemplateId(null);
    setFormData({ tenantId, name: '', channel: 'SMS', subject: '', body: '', category: '' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingTemplateId) {
        await messagingApi.updateTemplate(editingTemplateId, formData);
      } else {
        await messagingApi.createTemplate(formData);
      }
      setEditingTemplateId(null);
      setFormData({ tenantId, name: '', channel: 'SMS', subject: '', body: '', category: '' });
      await loadTemplates();
      setError(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id: string, name: string) => {
    setDeleteTemplateCandidate({ id, name });
  };

  const confirmDeleteTemplate = async () => {
    if (!deleteTemplateCandidate) return;
    try {
      await messagingApi.deleteTemplate(deleteTemplateCandidate.id);
      await loadTemplates();
      setDeleteTemplateCandidate(null);
    } catch (err: any) {
      setError(err?.message || 'Failed to delete template');
      setDeleteTemplateCandidate(null);
    }
  };

  return (
    <div className="p-6">
      <div className="rounded-3xl border border-gray-200 bg-white shadow-sm dark:border-gray-800 dark:bg-gray-950 overflow-hidden">
        <div className="flex flex-col gap-4 p-6 md:flex-row md:items-end md:justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Message Templates</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">Create reusable SMS and email content for your batches.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => onNavigate?.('message-batches')}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700"
            >
              View Batches
            </button>
            <button
              type="button"
              onClick={() => onNavigate?.('providers')}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
            >
              Providers
            </button>
          </div>
        </div>

        {error && (
          <div className="mx-6 mb-4 rounded-2xl bg-red-100 px-4 py-3 text-red-700 dark:bg-red-900/40 dark:text-red-200 border border-red-400 dark:border-red-700">
            {error}
          </div>
        )}

        <div className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr] p-6">
          <div className="rounded-3xl border border-gray-200 bg-gray-50 p-6 dark:border-gray-700 dark:bg-gray-900">
            <h2 className="font-semibold text-lg mb-3 text-gray-900 dark:text-white">Template library</h2>
            {loading ? (
              <div className="text-gray-700 dark:text-gray-200">Loading templates...</div>
            ) : templates.length === 0 ? (
              <div className="text-gray-600 dark:text-gray-400">No templates yet. Create one to reuse later.</div>
            ) : (
              <div className="space-y-3">
                {templates.map((template) => (
                  <div key={template.id} className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-950">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">{template.name}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{template.channel} • {template.category || 'General'}</p>
                      </div>
                      <div className="flex gap-2 items-center">
                      <button
                        type="button"
                        onClick={() => handleEditTemplate(template)}
                        className="text-indigo-600 hover:text-indigo-800 dark:text-indigo-300 dark:hover:text-indigo-100 text-sm"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(template.id, template.name)}
                        className="text-red-600 hover:text-red-800 dark:text-red-300 dark:hover:text-red-100 text-sm"
                      >
                        Delete
                      </button>
                    </div>
                    </div>
                    <div className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {template.body}
                    </div>
                    {template.subject && (
                      <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">Subject: {template.subject}</div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-3xl border border-gray-200 bg-white p-6 dark:border-gray-700 dark:bg-gray-900">
            <div className="flex items-center justify-between gap-4">
              <h2 className="font-semibold text-lg mb-3 text-gray-900 dark:text-white">
                {editingTemplateId ? 'Edit template' : 'Create new template'}
              </h2>
              {editingTemplateId && (
                <button
                  type="button"
                  onClick={handleCancelEdit}
                  className="text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  Cancel edit
                </button>
              )}
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block font-semibold mb-2 text-gray-900 dark:text-white">Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="border rounded-lg bg-white text-gray-900 px-3 py-2 w-full border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white dark:border-gray-700"
                  placeholder="Holiday Offer"
                />
              </div>

              <div>
                <label className="block font-semibold mb-2 text-gray-900 dark:text-white">Channel *</label>
                <select
                  name="channel"
                  value={formData.channel}
                  onChange={handleChange}
                  className="border rounded-lg bg-white text-gray-900 px-3 py-2 w-full border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white dark:border-gray-700"
                >
                  <option value="SMS">SMS</option>
                  <option value="EMAIL">Email</option>
                </select>
              </div>

              {formData.channel === 'EMAIL' && (
                <div>
                  <label className="block font-semibold mb-2 text-gray-900 dark:text-white">Subject</label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className="border rounded-lg bg-white text-gray-900 px-3 py-2 w-full border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white dark:border-gray-700"
                    placeholder="Special offer details"
                  />
                </div>
              )}

              <div>
                <label className="block font-semibold mb-2 text-gray-900 dark:text-white">Message body *</label>
                <textarea
                  name="body"
                  value={formData.body}
                  onChange={handleChange}
                  required
                  rows={6}
                  className="border rounded-lg bg-white text-gray-900 px-3 py-2 w-full border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white dark:border-gray-700"
                  placeholder="Hello {{firstName}}, enjoy 20% off on your next order"
                />
              </div>

              <div>
                <label className="block font-semibold mb-2 text-gray-900 dark:text-white">Category</label>
                <input
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  className="border rounded-lg bg-white text-gray-900 px-3 py-2 w-full border-gray-300 focus:border-indigo-500 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white dark:border-gray-700"
                  placeholder="Promotions, Reminders, Support"
                />
              </div>

              <button
                type="submit"
                disabled={saving}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              >
                {saving ? 'Saving...' : editingTemplateId ? 'Update Template' : 'Save Template'}
              </button>
            </form>
          </div>
        </div>
      </div>
      {deleteTemplateCandidate && (
        <ConfirmationModal
          title="Delete Template"
          message={`Are you sure you want to delete the template \"${deleteTemplateCandidate.name}\"? This cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonClass="bg-red-600 hover:bg-red-700"
          onConfirm={confirmDeleteTemplate}
          onClose={() => setDeleteTemplateCandidate(null)}
        />
      )}
    </div>
  );
}
